import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", service_interest: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const services = ["Programmable LED Display", "Light Box", "Channel Letters", "Neon Sign", "Non-Illuminated Sign", "Non-Illuminated 3D Letters", "Pylon Sign", "Window/Car/Wall Graphics", "Banners", "Sign Repair", "Electrical Services", "Sign Installation", "City Sign Permit", "Other"];

  return (
    <div data-testid="contact-page">
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">
            Get In Touch
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6">
            Contact <span className="gold-shimmer">Us</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-lg text-white/50 max-w-2xl">
            Have a project in mind? Reach out and let's discuss how we can help your business stand out.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div className="glass-card rounded-2xl p-8">
                <h3 className="font-heading text-xl font-bold text-white mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <a href="tel:+17783220801" data-testid="contact-phone" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-gold" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Phone</p>
                      <p className="text-white group-hover:text-gold transition-colors">(778) 322-0801</p>
                    </div>
                  </a>
                  <a href="https://maps.google.com/?q=780+E+Cordova+St,+Vancouver,+BC+V6A+1M3" target="_blank" rel="noopener noreferrer" data-testid="contact-address" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-gold" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Address</p>
                      <p className="text-white group-hover:text-gold transition-colors">780 E Cordova St, Vancouver, BC V6A 1M3</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-gold" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Business Hours</p>
                      <p className="text-white">Mon - Sun: 4PM - 9PM</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-8">
                <h3 className="font-heading text-lg font-bold text-white mb-4">Prefer to Book a Consultation?</h3>
                <p className="text-white/50 text-sm mb-6">Schedule a free on-site consultation at your convenience.</p>
                <Link to="/booking" data-testid="contact-booking-link" className="inline-flex items-center gap-2 bg-gold text-black font-bold px-6 py-3 rounded-full text-sm hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all">
                  Book Consultation <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8" data-testid="contact-form">
                <h3 className="font-heading text-xl font-bold text-white mb-8">Send Us a Message</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-white/60 text-xs block mb-2">First Name *</label>
                    <input
                      type="text"
                      data-testid="contact-first-name"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs block mb-2">Last Name</label>
                    <input
                      type="text"
                      data-testid="contact-last-name"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-white/60 text-xs block mb-2">Email *</label>
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs block mb-2">Phone</label>
                    <input
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                      placeholder="(778) 000-0000"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-white/60 text-xs block mb-2">Service of Interest</label>
                  <select
                    data-testid="contact-service-select"
                    value={form.service_interest}
                    onChange={(e) => setForm({ ...form, service_interest: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                  >
                    <option value="" className="bg-[#131a23]">Select a service...</option>
                    {services.map((s) => (
                      <option key={s} value={s} className="bg-[#131a23]">{s}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-6">
                  <label className="text-white/60 text-xs block mb-2">Message *</label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={5}
                    className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>
        {/* Not for people. Hidden, unlabelled, skipped by the tab key and
            excluded from autofill - a bot fills every input it finds in the
            HTML, and filling this one is the giveaway. The server bins
            anything that arrives with it set. */}
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
          data-testid="spam-trap"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="w-full bg-gold text-black font-bold py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? "Sending..." : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="glass-card rounded-2xl overflow-hidden">
            <iframe
              title="VANSIGN Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2602.3!2d-123.0847!3d49.2827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548671736b4e039f%3A0x0!2s780%20E%20Cordova%20St%2C%20Vancouver%2C%20BC%20V6A%201M3!5e0!3m2!1sen!2sca!4v1700000000000"
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
              data-testid="contact-map"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 section-alt border-t border-white/5" data-testid="contact-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5">
            Prefer a Face-to-Face Meeting?
          </h2>
          <p className="text-white/50 text-base mb-8">
            Book a free on-site consultation. We'll come to your location and provide a custom quote.
          </p>
          <Link to="/booking" data-testid="contact-page-cta-btn" className="inline-flex items-center gap-2 bg-gold text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1">
            Book Free Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
